import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/Button';
import { ChevronBackIcon, ChevronForwardIcon } from '@/components/Icons';

import { CarouselOverview } from './CarouselOverview/CarouselOverview';
import {
  StyledCardCarousel,
  StyledCardWrapper,
  StyledControls,
  StyledHorizontalSlider,
  StyledScrollFiller,
} from './styles';

interface CarouselProps {
  cards: {
    id: string;
    element: React.ReactNode;
  }[];
  startAtIndex?: number;
  cardWrapperHeightPx?: number;
  maxCardHeightPx?: number;
  showControls?: boolean;
  showOverview?: boolean;
  onFocusIndexChange?: (index: number) => void;
  alignControls?: 'left' | 'center' | 'right';
  fullWidthControls?: boolean;
}

function Carousel({
  cards,
  startAtIndex = 0,
  maxCardHeightPx = 800,
  cardWrapperHeightPx,
  showControls = true,
  showOverview = true,
  alignControls = 'center',
  onFocusIndexChange = () => {},
  fullWidthControls = false,
}: CarouselProps) {
  const cardSliderAreaRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<HTMLDivElement[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);

  // Call onFocusIndexChange when focusedIndex changes so parent components can react to the change
  useEffect(() => {
    onFocusIndexChange(focusedIndex);
  }, [focusedIndex, onFocusIndexChange]);

  // The focusedIndex will be updated automatically through onScroll events
  const scrollToIndex = useCallback(
    (cardIndex: number) => {
      if (!cardSliderAreaRef.current || !cardRefs.current) return;
      const targetCard = cardRefs.current[cardIndex];
      if (targetCard) {
        const scrollTarget =
          targetCard.offsetLeft + targetCard.clientWidth / 2 - cardSliderAreaRef.current.clientWidth / 2;
        // Scroll horizontally without affecting the vertical position

        cardSliderAreaRef.current.scrollLeft = scrollTarget;
      }
    },
    [cardRefs, cardSliderAreaRef],
  );

  const scrollToNextCard = useCallback(() => {
    scrollToIndex(focusedIndex + 1);
  }, [focusedIndex, scrollToIndex]);

  const scrollToPreviousCard = useCallback(() => {
    scrollToIndex(focusedIndex - 1);
  }, [focusedIndex, scrollToIndex]);

  // Initial focus
  useEffect(() => {
    scrollToIndex(startAtIndex);
  }, [scrollToIndex, startAtIndex]);

  // Determine the focused card based on the horizontal scroll position
  const handleScroll = useCallback(() => {
    if (!cardSliderAreaRef.current) return;

    const sliderArea = cardSliderAreaRef.current;
    const centerOfSliderArea = sliderArea.scrollLeft + sliderArea.offsetWidth / 2;

    let closestIndex = 0;
    let smallestDistance = Infinity;

    // Find the card that is closest to the center of the sliderArea
    cardRefs.current!.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardCenter - centerOfSliderArea);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestIndex = index;
      }
    });

    setFocusedIndex(closestIndex);
  }, [setFocusedIndex, cardRefs, cardSliderAreaRef]);

  // Register the scroll event listener
  useEffect(() => {
    const slider = cardSliderAreaRef.current;
    if (!slider) return () => {};

    slider.addEventListener('scroll', handleScroll);

    return () => {
      slider.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <StyledCardCarousel>
      <StyledHorizontalSlider ref={cardSliderAreaRef} $height={cardWrapperHeightPx}>
        <StyledScrollFiller />
        {cards.map((card, index) => (
          <StyledCardWrapper
            $maxHeightPx={maxCardHeightPx}
            key={card.id}
            ref={(targetCard: HTMLDivElement) => {
              cardRefs.current[index] = targetCard;
            }}
            onClick={() => scrollToIndex(index)}
          >
            {card.element}
          </StyledCardWrapper>
        ))}
        <StyledScrollFiller />
      </StyledHorizontalSlider>
      {showControls && (
        <StyledControls $align={alignControls} $fullWidth={fullWidthControls}>
          <Button variant="secondary" round chevron onClick={scrollToPreviousCard}>
            <ChevronBackIcon />
          </Button>
          {showOverview && <CarouselOverview cards={cards} activeIndex={focusedIndex} />}

          <Button variant="secondary" round chevron onClick={scrollToNextCard}>
            <ChevronForwardIcon />
          </Button>
        </StyledControls>
      )}
    </StyledCardCarousel>
  );
}

export { Carousel };
